import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { useDelivery } from "../hooks/useDelivery";
import Header from "../components/Header";
import { ChevronLeft, Banknote, CreditCard, Loader2 } from "lucide-react";

export default function CheckoutPage() {
  const { t, cart, subtotal, guestId, clearCart, addOrderRef, city } = useApp();
  const navigate = useNavigate();
  const { feeFor } = useDelivery();
  const fee = feeFor(subtotal);
  const total = subtotal + fee;
  const hasAge = cart.some((i) => i.age_restricted);

  const [form, setForm] = useState({
    first: "", last: "", street: "", postal: "", phone: "", email: "", note: "",
    city: city?.replace(/^Norrköping.*$/, "Norrköping") || "Norrköping",
  });
  const [method, setMethod] = useState("cash");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (cart.length === 0) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <p className="text-muted-foreground mb-4">{t.emptyCart}</p>
          <button onClick={() => navigate("/")} className="h-11 px-5 rounded-xl bg-primary text-primary-foreground font-semibold">{t.backHome}</button>
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!form.first || !form.street || !form.phone) { toast.error(t.fillRequired); return; }
    if (hasAge && !ageConfirmed) { toast.error(t.mustConfirmAge); return; }
    setSubmitting(true);
    try {
      const dropoff = `${form.street}, ${form.postal} ${form.city}`.trim();
      const payload = {
        dropoff_address: dropoff,
        recipient_first_name: form.first,
        recipient_last_name: form.last,
        street: form.street,
        postal_code: form.postal,
        city: form.city,
        phone: form.phone,
        email: form.email || null,
        item_description: cart.map((i) => `${i.qty}x ${i.name}`).join(", "),
        cart_items: cart.map((i) => ({ store: i.store_name, name: i.name, qty: i.qty, price: i.price, image_path: null, note: i.note || null })),
        items_subtotal: subtotal,
        notes: form.note || null,
        payment_method: method,
        amount: total,
        guest_id: guestId,
        age_restricted: hasAge,
        age_confirmed: hasAge ? ageConfirmed : false,
      };
      const order = await api.createOrder(payload);
      addOrderRef({ order_id: order.order_id, order_number: order.order_number, created_at: order.created_at });
      clearCart();
      toast.success(t.orderCreated, { description: order.order_number });
      navigate(`/track/${order.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key, label, type = "text", extra = "") => (
    <div className={extra}>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <input
        data-testid={`checkout-${key}`}
        type={type}
        value={form[key]}
        onChange={set(key)}
        className="mt-1 w-full h-11 rounded-xl border border-border bg-card px-3 outline-none focus:ring-2 focus:ring-primary/40"
      />
    </div>
  );

  return (
    <div className="min-h-screen pb-10">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="checkout-back" onClick={() => navigate("/")} className="mb-4 h-9 px-3 rounded-xl border border-border text-sm font-semibold flex items-center gap-1 hover:bg-accent">
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" /> {t.back}
        </button>
        <h1 className="font-head text-2xl font-extrabold mb-5">{t.checkout}</h1>

        <section className="rounded-2xl border border-border bg-card p-5 mb-4">
          <h2 className="font-head font-bold mb-4">{t.recipient}</h2>
          <div className="grid grid-cols-2 gap-3">
            {field("first", t.firstName)}
            {field("last", t.lastName)}
            {field("street", t.street, "text", "col-span-2")}
            {field("postal", t.postal)}
            {field("city", t.cityLabel)}
            {field("phone", t.phone, "tel")}
            {field("email", t.email, "email")}
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-muted-foreground">{t.orderNote}</label>
            <textarea data-testid="checkout-note" value={form.note} onChange={set("note")} rows={2} className="mt-1 w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 mb-4">
          <h2 className="font-head font-bold mb-4">{t.payment}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button data-testid="payment-cash" onClick={() => setMethod("cash")} className={`text-start p-4 rounded-2xl border transition-all ${method === "cash" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:bg-accent"}`}>
              <Banknote className="h-6 w-6 text-primary mb-2" />
              <div className="font-semibold">{t.cash}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.cashDesc}</div>
            </button>
            <button data-testid="payment-card" onClick={() => setMethod("card")} className={`text-start p-4 rounded-2xl border transition-all ${method === "card" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:bg-accent"}`}>
              <CreditCard className="h-6 w-6 text-primary mb-2" />
              <div className="font-semibold">{t.card}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.cardDesc}</div>
            </button>
          </div>
        </section>

        {hasAge && (
          <label data-testid="checkout-age-confirm" className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 mb-4 cursor-pointer">
            <input type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-1 h-5 w-5 accent-[hsl(var(--primary))]" />
            <span className="text-sm font-medium">{t.ageConfirmCheckout}</span>
          </label>
        )}

        <section className="rounded-2xl border border-border bg-card p-5 mb-4 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">{t.subtotal}</span><span>{subtotal} {t.kr}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t.deliveryFee}</span><span>{fee === 0 ? t.free : `${fee} ${t.kr}`}</span></div>
          <div className="flex justify-between font-bold text-lg pt-1"><span>{t.total}</span><span>{total} {t.kr}</span></div>
        </section>

        <button
          data-testid="place-order-button"
          disabled={submitting}
          onClick={submit}
          className="w-full h-13 min-h-[3.25rem] rounded-2xl bg-primary text-primary-foreground font-bold text-base hover:opacity-90 transition-opacity shadow-lg shadow-primary/25 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> {t.placing}</> : `${t.placeOrder} · ${total} ${t.kr}`}
        </button>
      </main>
    </div>
  );
}
